// Route: /impersonate?token=<hmac> — обмен impersonation-токена на сессию.
//
// Поток (rules/api-layer.md §8i): superadmin в админке жмёт «Войти как
// пользователь» → открывается этот URL в новой вкладке web. Здесь POST'им token
// в /api/admin/impersonate/exchange, получаем реальную Better Auth сессию target
// (cookie + Bearer), сохраняем Bearer в auth-storage и делаем full reload на /
// (чистая гидратация сессии — как native-auth-callback).
//
// Single-use: токен одноразовый (server enforce'ит jti в Redis). Дедуп по token
// через sessionStorage защищает от двойного POST при StrictMode/повторном mount
// (второй POST → 401, ложная ошибка). useEffect с [] deps + stable guard
// (rules/frontend-state.md §7 — OAuth/callback effect стабилизация).

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button } from "@studyhub/ui/components/button";
import { IconLoader2, IconUserExclamation } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { NoIndexMeta } from "@/components/seo/NoIndexMeta";
import { setAccessToken } from "@/lib/auth-storage";

const API_BASE = import.meta.env.VITE_API_URL ?? "";
const ADMIN_URL = import.meta.env.VITE_ADMIN_URL ?? "";
const PROCESSED_KEY = "studyhub.impersonate-processed-token";

const searchSchema = z.object({
	token: z.string().optional(),
});

export const Route = createFileRoute("/impersonate")({
	validateSearch: tolerantSearch(searchSchema),
	component: ImpersonatePage,
});

type Phase = "processing" | "error";

async function exchangeToken(token: string): Promise<boolean> {
	try {
		const resp = await fetch(`${API_BASE}/api/admin/impersonate/exchange`, {
			method: "POST",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ token }),
		});
		if (!resp.ok) return false;
		const data = (await resp.json().catch(() => null)) as {
			token?: string;
			redirectTo?: string;
		} | null;
		if (data?.token) {
			// Bearer — для cross-origin (dev :5173↔:3000) и Capacitor; cookie уже
			// выставлена ответом для prod cross-subdomain. Full reload → чистая сессия.
			setAccessToken(data.token);
			window.location.replace(data.redirectTo ?? "/");
			return true;
		}
		return false;
	} catch {
		return false;
	}
}

function ImpersonatePage(): React.ReactElement {
	const { t } = useTranslation("auth");
	const { token } = Route.useSearch();
	const [phase, setPhase] = useState<Phase>("processing");

	// sessionStorage-дедуп: токен одноразовый, повторный POST → 401. token стабилен
	// в URL на время жизни route; дедуп защищает от StrictMode/двойного mount
	// (rules/frontend-state.md §7 — стабилизация callback-effect).
	useEffect(() => {
		if (!token) {
			setPhase("error");
			return;
		}
		let processed = false;
		try {
			processed = sessionStorage.getItem(PROCESSED_KEY) === token;
		} catch {
			// приватный режим — дедуп не сохранится, не критично
		}
		if (processed) {
			setPhase("error");
			return;
		}
		try {
			sessionStorage.setItem(PROCESSED_KEY, token);
		} catch {
			// ignore
		}
		void exchangeToken(token).then((ok) => {
			if (!ok) setPhase("error");
			// при ok — exchangeToken уже сделал window.location.replace
		});
	}, [token]);

	return (
		<div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
			<NoIndexMeta />
			{phase === "processing" ? (
				<>
					<IconLoader2 className="size-8 animate-spin text-muted-foreground" />
					<p className="text-sm text-muted-foreground">{t("impersonation.processing")}</p>
				</>
			) : (
				<div className="flex max-w-sm flex-col items-center gap-3">
					<div className="flex size-12 items-center justify-center rounded-full bg-destructive/10">
						<IconUserExclamation className="size-6 text-destructive" />
					</div>
					<h1 className="text-lg font-semibold">{t("impersonation.errorTitle")}</h1>
					<p className="text-sm text-muted-foreground">{t("impersonation.errorHelp")}</p>
					{ADMIN_URL && (
						<Button variant="outline" className="cursor-pointer" asChild>
							{/* Внешний домен админки — голый <a>, не <Link> (rules/frontend-quality.md §11.2). */}
							<a href={ADMIN_URL}>{t("impersonation.backToAdmin")}</a>
						</Button>
					)}
				</div>
			)}
		</div>
	);
}
